import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useState, type FormEvent } from 'react';

import { kinds, type EnvKey } from '@/app/site/scope';
import type { OAuthProviderBody } from '@/shared/api/generated/model';
import { siteOAuthList, siteOAuthSet } from '@/shared/api/generated/site-users/site-users';
import { ApiError } from '@/shared/api/http';
import { Badge } from '@/shared/ui/Badge';
import { Button } from '@/shared/ui/Button';
import { CopyButton } from '@/shared/ui/CopyButton';
import { Field } from '@/shared/ui/Field';
import { Input } from '@/shared/ui/Input';
import { Switch } from '@/shared/ui/Switch';
import { toast } from '@/shared/ui/Toast';

import styles from './UsersPage.module.scss';

/** Где завести приложение у провайдера — словами. */
const WHERE: Record<OAuthProviderBody['provider'], string> = {
  google:
    'Google Cloud Console → Google Auth Platform → Clients → Web application. Пока приложение в режиме Testing, войдут только тестовые пользователи из Audience.',
  vk: 'VK ID → приложение типа «Веб» — нужен только ID приложения, секрет не используется. В «Базовые домены» добавьте и домен сайта: VK сверяет страницу, с которой пришёл посетитель.',
  yandex: 'Яндекс OAuth → новое приложение для веб-сервисов с доступом к почте и имени.',
};

/**
 * Вход через соцсети (шаг 9.15): ключи приложения у Google, VK ID и Яндекс ID.
 * Свои у каждого окружения — переносом с Development не копируются. Секрет
 * не показывается: пусто — остаётся прежний.
 */
export function OAuthSettings({ siteId, env }: { siteId: string; env: EnvKey }) {
  const key = ['site-oauth', siteId, env];

  const list = useQuery({
    queryKey: key,
    queryFn: async () => {
      const response = await siteOAuthList(siteId, kinds[env]);

      if (response.status !== 200) {
        throw new Error('Неожиданный ответ на список провайдеров');
      }

      return response.data.items ?? [];
    },
  });

  if (list.data === undefined) {
    return null;
  }

  return (
    <section className={styles['form']} aria-label="Вход через соцсети">
      <div>
        <h2 className={styles['section-title']}>Вход через соцсети</h2>
        <p className={styles['muted']}>
          Кнопки входа на сайте. Приложение у провайдера заводите сами и вписываете в него адрес
          возврата; ключи — свои у каждого окружения. Сайт показывает кнопки по
          faceta.auth.oauthProviders().
        </p>
      </div>
      {list.data.map((p) => (
        <ProviderForm
          key={`${p.provider}-${p.client_id}-${String(p.enabled)}`}
          siteId={siteId}
          env={env}
          p={p}
        />
      ))}
    </section>
  );
}

function ProviderForm({ siteId, env, p }: { siteId: string; env: EnvKey; p: OAuthProviderBody }) {
  const queryClient = useQueryClient();
  const [enabled, setEnabled] = useState(p.enabled);
  const [clientId, setClientId] = useState(p.client_id);
  const [secret, setSecret] = useState('');
  const [error, setError] = useState<string>();
  const [busy, setBusy] = useState(false);

  const submit = (event: FormEvent) => {
    event.preventDefault();
    setBusy(true);
    setError(undefined);

    siteOAuthSet(siteId, kinds[env], p.provider, {
      enabled,
      client_id: clientId.trim(),
      ...(secret.trim() === '' ? {} : { client_secret: secret.trim() }),
    })
      .then((response) => {
        if (response.status === 200) {
          queryClient.setQueryData(['site-oauth', siteId, env], response.data.items ?? []);
        }

        toast.success(`${p.name}: сохранено`);
      })
      .catch((cause: unknown) => {
        setError(cause instanceof ApiError ? cause.detail : 'Не удалось сохранить');
      })
      .finally(() => {
        setBusy(false);
      });
  };

  return (
    <form className={styles['provider']} onSubmit={submit} noValidate>
      <div className={styles['provider__head']}>
        <strong>{p.name}</strong>
        {p.ready ? (
          <Badge size="sm" tone="success">
            кнопка на сайте
          </Badge>
        ) : (
          <Badge size="sm" tone="neutral">
            выключен
          </Badge>
        )}
        <div className={styles['provider__end']}>
          <Switch checked={enabled} onCheckedChange={setEnabled}>
            Включить
          </Switch>
        </div>
      </div>

      <p className={styles['muted']}>{WHERE[p.provider]}</p>

      {/* Переключатель выглядит мгновенным, но сохраняется только кнопкой —
          без подсказки легко уйти с включённым на экране и выключенным на сайте. */}
      {enabled !== p.enabled ? (
        <p className={styles['oauth-warn']}>
          {enabled ? 'Вход включён, но не сохранён' : 'Вход выключен, но не сохранён'} — нажмите
          «Сохранить».
        </p>
      ) : (
        !p.enabled &&
        (p.client_id !== '' || p.client_secret_set) && (
          <p className={styles['oauth-warn']}>
            Ключи сохранены, но вход выключен — кнопки на сайте нет. Включите и сохраните.
          </p>
        )
      )}

      <Field
        label="Адрес возврата"
        hint="Вписать в настройках приложения у провайдера как redirect URI"
      >
        <div className={styles['copy']}>
          <Input mono readOnly value={p.callback_url} />
          <CopyButton value={p.callback_url} label="Скопировать адрес" />
        </div>
      </Field>

      <div className={styles['row']}>
        <Field label={p.provider === 'vk' ? 'ID приложения' : 'Идентификатор клиента'}>
          <Input
            mono
            value={clientId}
            onChange={(event) => {
              setClientId(event.target.value);
            }}
          />
        </Field>
        {p.needs_secret && (
          <Field
            label="Секрет"
            hint={p.client_secret_set ? 'Задан — оставьте пустым, чтобы не менять' : 'Не задан'}
          >
            <Input
              mono
              type="password"
              autoComplete="off"
              value={secret}
              onChange={(event) => {
                setSecret(event.target.value);
              }}
            />
          </Field>
        )}
      </div>

      {error !== undefined && <p className={styles['error']}>{error}</p>}

      <div>
        <Button type="submit" size="sm" variant="outline" loading={busy}>
          Сохранить
        </Button>
      </div>
    </form>
  );
}
